import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import ImageWithFallback from '../../components/ui/ImageWithFallback'

const countries = [
  'Kenya', 'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Japan', 
  'Australia', 'Singapore', 'Hong Kong', 'China', 'India', 'South Korea',
  'Netherlands', 'Sweden', 'Switzerland', 'Other'
]

export default function RegisterPage() {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { register: registerUser } = useAuth()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
  } = useForm()

  const password = watch('password')

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      const result = await registerUser({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        organization: data.organization,
        country: data.country,
        age: data.age,
        participationType: data.participationType,
      })
      
      if (result.success) {
        navigate('/dashboard')
      } else {
        setError('root', { message: result.error })
      }
    } catch (error) {
      setError('root', { message: 'An unexpected error occurred' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center aurora-bg py-12 px-4 sm:px-6 lg:px-8 relative">
      <div className="absolute inset-0 golden-mesh opacity-30"></div>
      
      {/* Floating decorative elements */}
      <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-primary rounded-full animate-floating-dots"></div>
      <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-primary rounded-full animate-sparkle" style={{animationDelay: '1s'}}></div>
      <div className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-primary/60 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
      
      <div className="max-w-lg w-full space-y-8 relative z-10">
        {/* Header Section */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <ImageWithFallback
                src="/images/zova_labs_right.png"
                alt="ZovaLabs Logo"
                className="w-16 h-16 object-contain animate-gold-pulse"
              />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full animate-sparkle"></div>
            </div>
          </div>
          
          <h1 className="text-4xl font-display font-bold text-foreground mb-2">
            Join ZovaLabs
          </h1>
          <p className="text-muted-foreground mb-6">
            Start your AI innovation journey today
          </p>
          
          <p className="text-sm text-muted-foreground">
            Already have an account?{' '}
            <Button variant="link" asChild className="p-0 h-auto font-medium">
              <Link to="/login">
                Sign in here
              </Link>
            </Button>
          </p>
        </div>
        {/* Registration Form Card */}
        <Card variant="luxury" className="shadow-2xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-display text-center">Create Account</CardTitle>
            <CardDescription className="text-center">
              Fill in your details to get started
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-sm font-medium">
                    First Name
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="John"
                    className={errors.firstName ? "border-destructive" : ""}
                    {...register('firstName', {
                      required: 'First name is required',
                      minLength: {
                        value: 2,
                        message: 'First name must be at least 2 characters',
                      },
                    })}
                  />
                  {errors.firstName && (
                    <p className="text-sm text-destructive">{errors.firstName.message}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-sm font-medium">
                    Last Name
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Doe"
                    className={errors.lastName ? "border-destructive" : ""}
                    {...register('lastName', {
                      required: 'Last name is required',
                      minLength: {
                        value: 2,
                        message: 'Last name must be at least 2 characters',
                      },
                    })}
                  />
                  {errors.lastName && (
                    <p className="text-sm text-destructive">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="john.doe@example.com"
                  className={errors.email ? "border-destructive" : ""}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              {/* Username Field */}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium">
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="johndoe123"
                  className={errors.username ? "border-destructive" : ""}
                  {...register('username', {
                    required: 'Username is required',
                    minLength: {
                      value: 3,
                      message: 'Username must be at least 3 characters',
                    },
                    maxLength: {
                      value: 30,
                      message: 'Username must be less than 30 characters',
                    },
                    pattern: {
                      value: /^[a-zA-Z0-9_]+$/,
                      message: 'Username can only contain letters, numbers, and underscores',
                    },
                  })}
                />
                {errors.username && (
                  <p className="text-sm text-destructive">{errors.username.message}</p>
                )}
              </div>

              {/* Organization Field */}
              <div className="space-y-2">
                <Label htmlFor="organization" className="text-sm font-medium">
                  Organization <span className="text-muted-foreground">(Optional)</span>
                </Label>
                <Input
                  id="organization"
                  type="text"
                  placeholder="Your company or university"
                  {...register('organization')}
                />
              </div>

              {/* Country Field */}
              <div className="space-y-2">
                <Label htmlFor="country" className="text-sm font-medium">
                  Country <span className="text-muted-foreground">(Optional)</span>
                </Label>
                <Select onValueChange={(value) => register('country').onChange({ target: { value } })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Age and Participation Type Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="age" className="text-sm font-medium">
                    Age
                  </Label>
                  <Input
                    id="age"
                    type="number"
                    min="13"
                    max="100"
                    placeholder="25"
                    className={errors.age ? "border-destructive" : ""}
                    {...register('age', {
                      required: 'Age is required',
                      min: {
                        value: 13,
                        message: 'You must be at least 13 years old',
                      },
                      max: {
                        value: 100,
                        message: 'Please enter a valid age',
                      },
                    })}
                  />
                  {errors.age && (
                    <p className="text-sm text-destructive">{errors.age.message}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="participationType" className="text-sm font-medium">
                    Participation Type
                  </Label>
                  <Select onValueChange={(value) => register('participationType').onChange({ target: { value } })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose preference" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="team">Team</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.participationType && (
                    <p className="text-sm text-destructive">{errors.participationType.message}</p>
                  )}
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Enter your password"
                    className={`pr-10 ${errors.password ? "border-destructive" : ""}`}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters',
                      },
                    })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4 text-muted-foreground hover:text-primary" />
                    ) : (
                      <EyeIcon className="h-4 w-4 text-muted-foreground hover:text-primary" />
                    )}
                  </Button>
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Confirm your password"
                    className={`pr-10 ${errors.confirmPassword ? "border-destructive" : ""}`}
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) =>
                        value === password || 'Passwords do not match',
                    })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-4 w-4 text-muted-foreground hover:text-primary" />
                    ) : (
                      <EyeIcon className="h-4 w-4 text-muted-foreground hover:text-primary" />
                    )}
                  </Button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
                )}
              </div>

              {/* Error Alert */}
              {errors.root && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {errors.root.message}
                  </AlertDescription>
                </Alert>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                variant="gold"
                size="lg"
                className="w-full shadow-2xl"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="sm" color="text-primary-foreground" />
                    <span className="ml-2">Creating Account...</span>
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>

              {/* Terms and Privacy */}
              <div className="text-xs text-muted-foreground text-center pt-4 border-t border-border">
                By creating an account, you agree to our{' '}
                <Button variant="link" asChild className="p-0 h-auto text-xs">
                  <Link to="/terms">Terms of Service</Link>
                </Button>{' '}
                and{' '}
                <Button variant="link" asChild className="p-0 h-auto text-xs">
                  <Link to="/privacy">Privacy Policy</Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}